import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Modal,
  Button,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  addMonths,
  subMonths,
  isSameDay,
} from "date-fns";
import { DateTime } from "luxon";
import MeetingCard from "./MeetingCard";
import SidebarSummary from "./SidebarSummary";

const ZOOM_SESSIONS_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/ZoomSessions";
const COURSES_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/Courses";
const READING_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/ReadingMaterials";

const formatSessionTime = (dateStr, timeStr, userTz) => {
  // dateStr: "27-May-25"
  // timeStr: "8pm - 10pm" or "11:30am - 1:30pm"
  const [startRaw, endRaw] = timeStr.toLowerCase().split(" - ");
  const startTime = parseTime(startRaw, dateStr, userTz);
  const endTime = parseTime(endRaw, dateStr, userTz);

  const formattedDate = startTime.toFormat("dd-MMM-yy");
  const formattedRange = `${startTime.toFormat("h:mma")} - ${endTime.toFormat(
    "h:mma"
  )}`;

  return { formattedDate, formattedRange };
};
const parseTime = (time, dateStr, userTz) => {
  // Normalize time input
  let normalizedTime = time.trim().toLowerCase();
  normalizedTime = normalizedTime.replace(/am|pm/, (match) =>
    match.toUpperCase()
  );

  if (!normalizedTime.includes(":")) {
    normalizedTime = normalizedTime.replace(/(AM|PM)$/, ":00$1");
  }

  const dateTimeFormats = ["dd-MMM-yy h:mma", "d-MMM-yy h:mma"];
  const dateTimeStr = `${dateStr} ${normalizedTime}`;

  let dt;
  for (const format of dateTimeFormats) {
    dt = DateTime.fromFormat(dateTimeStr, format, {
      zone: "Asia/Kolkata",
    });
    if (dt.isValid) break;
  }

  if (!dt || !dt.isValid) {
    console.error("Failed to parse datetime:", dateTimeStr);
    return null;
  }

  return dt.setZone(userTz);
};

const CalendarView = () => {
  const [sessions, setSessions] = useState([]);
  const [courseMeta, setCourseMeta] = useState({});
  const [readingMap, setReadingMap] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalSessions, setModalSessions] = useState([]);
  const [collapsePastWeeks, setCollapsePastWeeks] = useState(false);
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const sessionsForMonth = sessions.filter((s) => {
    const date = new Date(s.Date);
    return date >= monthStart && date <= monthEnd;
  });
  const courseColors = {};
  const palette = [
    "#FFEBEE",
    "#E8F5E9",
    "#E3F2FD",
    "#FFF3E0",
    "#F3E5F5",
    "#F9FBE7",
    "#E0F2F1",
    "#ECEFF1",
    "#FFFDE7",
    "#EDE7F6",
  ];
  useEffect(() => {
    const fetchData = async () => {
      const [sessionsRes, coursesRes, readingsRes] = await Promise.all([
        fetch(ZOOM_SESSIONS_URL),
        fetch(COURSES_URL),
        fetch(READING_URL),
      ]);
      const [sessionData, courseData, readingData] = await Promise.all([
        sessionsRes.json(),
        coursesRes.json(),
        readingsRes.json(),
      ]);

      const courseDict = {};
      courseData.forEach((c) => {
        courseDict[c.CourseId] = {
          email: c.FacultyEmail,
          phone: c.FacultyNumber,
        };
      });
      setCourseMeta(courseDict);

      const readingDict = {};
      readingData.forEach((item) => {
        if (!readingDict[item.CourseId]) readingDict[item.CourseId] = [];
        readingDict[item.CourseId].push({
          title: item.Title,
          link: item.ReadingMaterialLink,
        });
      });
      setReadingMap(readingDict);

      const formatted = sessionData.map((item) => ({
        ...item,
        dateObj: new Date(item.Date),
      }));
      setSessions(formatted);
    };

    fetchData();
  }, []);
  const getCourseColor = (courseId) => {
    if (!courseColors[courseId]) {
      const keys = Object.keys(courseColors);
      courseColors[courseId] = palette[keys.length % palette.length];
    }
    return courseColors[courseId];
  };
  const normalizeTitle = (title) => {
    return title
      ?.replace(/^\d+\.\s*/, "") // remove leading numbers like "1. "
      .replace(/\.[^/.]+$/, "") // remove file extension
      .toLowerCase()
      .trim();
  };
  const handleDateClick = (day) => {
    const dayStr = day.toDateString(); // normalize
    const filtered = sessions.filter(
      (s) => new Date(s.Date).toDateString() === dayStr
    );
    setSelectedDate(new Date(day)); // set fresh date object
    setModalSessions(filtered);
  };

  const renderHeader = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <Button
        variant="secondary"
        onClick={() => setCollapsePastWeeks(!collapsePastWeeks)}
      >
        {collapsePastWeeks ? "Show All Weeks" : "Hide Past Weeks"}
      </Button>
      <h4>{format(currentMonth, "MMMM yyyy")}</h4>
      <Button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
        Prev
      </Button>
      <Button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
        Next
      </Button>
    </div>
  );

  const renderDays = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return (
      <div className="calendar-row">
        {days.map((d, i) => (
          <div key={i} className="calendar-col calendar-day-header">
            {d}
          </div>
        ))}
      </div>
    );
  };
  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const today = new Date();

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      const weekEnd = addDays(day, 6);
      const weekIsPast = weekEnd < today;

      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const isToday = isSameDay(today, cloneDay);
        const formatted = format(cloneDay, "d");
        const todaySessions = sessions.filter((s) =>
          isSameDay(new Date(s.Date), cloneDay)
        );
        const bgColor = todaySessions.length
          ? getCourseColor(todaySessions[0].CourseId)
          : undefined;

        const tooltip = (
          <Tooltip id={`tooltip-${cloneDay.toISOString()}`}>
            {todaySessions.length === 0 ? (
              <span>No sessions</span>
            ) : (
              <div style={{ textAlign: "left" }}>
                {todaySessions.map((s, idx) => (
                  <div key={idx}>
                    <strong>{s.Class}</strong> <br />
                    <small>
                      {s.Time} with {s.Faculty}
                    </small>
                    {idx < todaySessions.length - 1 && <hr className="my-1" />}
                  </div>
                ))}
              </div>
            )}
          </Tooltip>
        );

        days.push(
          <OverlayTrigger
            key={cloneDay.toISOString()}
            placement="top"
            overlay={tooltip}
            delay={{ show: 200, hide: 100 }}
          >
            <div
              className={`calendar-col calendar-cell ${
                isToday ? "calendar-today" : ""
              }`}
              onClick={() => handleDateClick(cloneDay)}
              style={{ backgroundColor: bgColor }}
            >
              <div>{formatted}</div>
              {todaySessions.length > 0 && (
                <div className="calendar-session-count">
                  {todaySessions.length} session
                  {todaySessions.length > 1 ? "s" : ""}
                </div>
              )}
            </div>
          </OverlayTrigger>
        );

        day = addDays(day, 1);
      }

      if (!collapsePastWeeks || !weekIsPast) {
        rows.push(
          <div className="calendar-row" key={day.toISOString()}>
            {days}
          </div>
        );
      }
      days = [];
    }

    return <div>{rows}</div>;
  };
  const decodeGoogleRedirect = (url) => {
    try {
      const parsedUrl = new URL(url);
      if (
        parsedUrl.hostname === "www.google.com" &&
        parsedUrl.pathname === "/url"
      ) {
        const actualUrl = parsedUrl.searchParams.get("q");
        return actualUrl ? decodeURIComponent(actualUrl) : url;
      }
    } catch (e) {
      // If URL parsing fails, return as is
    }
    return url;
  };

  const formatRecordingLink = (url) => {
    if (!url) return "";
    const match = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([^/]+)\/(view|preview)/
    );
    return match ? `https://drive.google.com/file/d/${match[1]}/preview` : url;
  };

  const renderPrereads = (session) => {
    const courseId = session.CourseId;
    const prereadsRaw = session.Prereads?.trim();
    if (!prereadsRaw) return null; // skip if blank

    const prereads = prereadsRaw
      .split("\n")
      .map((p) => p.trim())
      .filter(Boolean);
    if (prereads.length === 0) return null;

    const readings = readingMap[courseId] || [];

    const matchedLinks = prereads.map((pre, idx) => {
      const normPre = normalizeTitle(pre);
      const match = readings.find((r) =>
        normalizeTitle(r.title).includes(normPre)
      );
      return match ? (
        <li key={`match-${idx}`}>
          <a href={match.link} target="_blank" rel="noreferrer">
            {match.title}
          </a>
        </li>
      ) : (
        <li key={`unmatched-${idx}`}>{pre}</li>
      );
    });

    return (
      <div>
        <strong>Prereads:</strong>
        <ul>{matchedLinks}</ul>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .force-margin {
          margin-top: 66px !important;
        }
        
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          border: 1px solid #dee2e6;
        }
        
        .calendar-cell {
          min-height: 100px;
          background-color: #fff;
          border: 1px solid #dee2e6;
          padding: 6px;
          font-size: 0.9rem;
          position: relative;
          transition: background-color 0.2s ease-in-out;
        }
        
        .calendar-cell:hover {
          background-color: #f1f9ff;
          cursor: pointer;
        }
        
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          text-align: center;
          font-weight: bold;
          margin-bottom: 0.5rem;
          border-bottom: 1px solid #ccc;
        }
        
        .calendar-session-count {
          font-size: 0.7em;
          color: #007bff;
          margin-top: 4px;
        }
        .calendar-day-header {
        font-weight: bold;
        text-align: center;
        padding: 8px 0;
        border: 1px solid #dee2e6;
        background-color: #f8f9fa;
      }

      .calendar-cell {
        border: 1px solid #dee2e6;
        padding: 10px;
        min-height: 100px;
        cursor: pointer;
        text-align: left;
        position: relative;
        transition: background-color 0.2s;
      }

      .calendar-cell:hover {
        background-color: #f1f9ff;
      }

      .calendar-session-count {
        font-size: 0.75em;
        color: #007bff;
        margin-top: 4px;
      }

      .calendar-row {
        display: flex;
        flex-wrap: nowrap;
      }

      .calendar-col {
        flex: 1;
        max-width: 14.2857%; /* 100 / 7 */
      }
      .calendar-today {
          border: 2px solid #007bff;
          background-color: #e9f5ff !important;
      }
      .calendar-cell:hover {
        box-shadow: 0 0 8px rgba(0,0,0,0.2);
        z-index: 2;
        position: relative;
      }
    `}</style>
      <Container className="mt-4 force-margin">
        <h2 className="mb-3">Calendar View</h2>

        <div className="d-flex">
  <div className="flex-grow-1">
    {renderHeader()}
    {renderDays()}
    {renderCells()}
  </div>
  <div
    style={{
      width: 300,
      display: "flex",
      flexDirection: "column",
       marginLeft: '16px', // add some space
    }}
  >
    <div
      style={{
        flex: 1,
        overflowY: "auto",
      }}
    >
      <SidebarSummary sessionsForMonth={sessionsForMonth} />
    </div>
  </div>
</div>
        <Modal
          show={!!selectedDate}
          onHide={() => setSelectedDate(null)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Sessions on {selectedDate && format(selectedDate, "PPP")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalSessions.length === 0 && <p>No sessions on this date.</p>}
            {modalSessions.map((s, idx) => (
              <MeetingCard
                key={idx}
                session={s}
                meta={courseMeta[s.CourseId] || {}}
                renderPrereads={renderPrereads}
                decodeGoogleRedirect={decodeGoogleRedirect}
                formatRecordingLink={formatRecordingLink}
                formatSessionTime={formatSessionTime}
              />
            ))}
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
};

export default CalendarView;
